import { useState, useEffect } from 'react'
import { useServerMonitoring } from '@/hooks/useServerMonitoring'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { 
  Activity, 
  AlertTriangle, 
  Info, 
  AlertCircle,
  Search,
  Filter,
  RefreshCw,
  Download,
  Clock
} from 'lucide-react'

interface LogEntry {
  timestamp: string
  level: 'info' | 'warn' | 'error'
  message: string
  source?: string
}

interface ActivityLogProps {
  serverId: string
  className?: string
  maxEntries?: number
}

export function ActivityLog({ serverId, className, maxEntries = 50 }: ActivityLogProps) {
  const { status, isLoading, refresh } = useServerMonitoring({
    serverId,
    enabled: true,
    interval: 15000 // Increased to 15 seconds to reduce API load
  })

  const [logs, setLogs] = useState<LogEntry[]>([])
  const [filteredLogs, setFilteredLogs] = useState<LogEntry[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [levelFilter, setLevelFilter] = useState<'all' | 'info' | 'warn' | 'error'>('all')
  const [autoScroll, setAutoScroll] = useState(true)

  // Update logs when status changes
  useEffect(() => {
    if (status?.logs) {
      setLogs(prevLogs => {
        // Merge new logs with existing ones, avoiding duplicates
        const newLogs = [...prevLogs]
        
        status.logs.forEach(newLog => {
          const exists = newLogs.some(log => 
            log.timestamp === newLog.timestamp && log.message === newLog.message
          )
          if (!exists) {
            newLogs.push(newLog)
          }
        })

        // Sort by timestamp and limit entries
        return newLogs
          .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime())
          .slice(0, maxEntries)
      })
    }
  }, [status?.logs, maxEntries])

  // Filter logs based on search and level
  useEffect(() => {
    let filtered = logs

    // Filter by level
    if (levelFilter !== 'all') {
      filtered = filtered.filter(log => log.level === levelFilter)
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(query) ||
        log.source?.toLowerCase().includes(query)
      )
    }

    setFilteredLogs(filtered)
  }, [logs, searchQuery, levelFilter])

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'error': return <AlertTriangle className="h-4 w-4 text-red-500" />
      case 'warn': return <AlertCircle className="h-4 w-4 text-yellow-500" />
      case 'info': return <Info className="h-4 w-4 text-blue-500" />
      default: return <Activity className="h-4 w-4 text-gray-500" />
    }
  }

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error': return 'border-l-red-500 bg-red-50'
      case 'warn': return 'border-l-yellow-500 bg-yellow-50'
      case 'info': return 'border-l-blue-500 bg-blue-50'
      default: return 'border-l-gray-500 bg-gray-50'
    }
  }

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString('en-US', { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    })
  }

  const exportLogs = () => {
    const logText = filteredLogs.map(log => 
      `[${log.timestamp}] ${log.level.toUpperCase()}: ${log.message} ${log.source ? `(${log.source})` : ''}`
    ).join('\n')
    
    const blob = new Blob([logText], { type: 'text/plain' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `server-${serverId}-logs-${new Date().toISOString().split('T')[0]}.txt`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center space-x-2">
            <Activity className="h-5 w-5" />
            <span>Activity Log</span>
          </CardTitle>
          <div className="flex items-center space-x-2">
            <Button variant="ghost" size="sm" onClick={exportLogs}>
              <Download className="h-4 w-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={refresh}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <CardDescription>Real-time server activity and event logs</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Filters */}
          <div className="flex items-center space-x-4">
            <div className="relative flex-1">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search logs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-8"
              />
            </div>
            <div className="flex items-center space-x-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value as any)}
                className="px-3 py-2 border rounded-md text-sm"
              >
                <option value="all">All Levels</option>
                <option value="info">Info</option>
                <option value="warn">Warning</option>
                <option value="error">Error</option>
              </select>
            </div>
          </div>

          {/* Log Stats */}
          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
            <div className="flex items-center space-x-1">
              <Clock className="h-4 w-4" />
              <span>Showing {filteredLogs.length} of {logs.length} entries</span>
            </div>
            <div className="flex items-center space-x-2">
              <Badge variant="outline">{logs.filter(l => l.level === 'error').length} errors</Badge>
              <Badge variant="outline">{logs.filter(l => l.level === 'warn').length} warnings</Badge>
              <Badge variant="outline">{logs.filter(l => l.level === 'info').length} info</Badge>
            </div>
          </div>

          {/* Logs List */}
          <div className="max-h-96 overflow-y-auto space-y-2">
            {isLoading && logs.length === 0 ? (
              <div className="text-center py-8">
                <RefreshCw className="h-6 w-6 animate-spin text-blue-500 mx-auto mb-2" />
                <p className="text-muted-foreground">Loading activity logs...</p>
              </div>
            ) : filteredLogs.length === 0 ? (
              <div className="text-center py-8">
                <Activity className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">
                  {searchQuery || levelFilter !== 'all' 
                    ? 'No logs match your filters' 
                    : 'No recent activity'}
                </p>
              </div>
            ) : (
              filteredLogs.map((log, index) => (
                <div
                  key={`${log.timestamp}-${index}`}
                  className={`border-l-4 p-3 rounded-r-md ${getLevelColor(log.level)}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-3 flex-1">
                      {getLevelIcon(log.level)}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 break-words">{log.message}</p>
                        {log.source && (
                          <p className="text-xs text-gray-600 mt-1">Source: {log.source}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 ml-4">
                      <Badge variant="outline" className="text-xs">
                        {log.level}
                      </Badge>
                      <span className="text-xs text-muted-foreground font-mono">
                        {formatTimestamp(log.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Auto-scroll toggle */}
          <div className="flex items-center justify-between text-sm text-muted-foreground">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="auto-scroll"
                checked={autoScroll}
                onChange={(e) => setAutoScroll(e.target.checked)}
                className="rounded border-gray-300"
              />
              <label htmlFor="auto-scroll">Auto-scroll to new entries</label>
            </div>
            {status && (
              <div>
                Last update: {formatTimestamp(status.last_activity)}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )
} 